import { FormEvent, useEffect, useState } from "react";
import { Employee, EmployeeAccount } from "../../models";
import { useData, useFeatures } from "../../utils/useData";
import {
  Autocomplete,
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { NavBackButton } from "./NavBackButton";
import { formatCurrency } from "../../utils/formatters";
import { toTitleCase } from "../../utils/toTitleCase";
import { LoadFeeTooltip } from "../checkout/Standard/Tooltips/LoadFeeTooltip";
import { useTopUpAccount } from "../../hooks/employeeAccounts/useTopUpAccount";

type AssignToPlanProps = {
  handleComplete: () => void;
  handleClose: () => void;
  selectedEmployees: Employee[];
};

export const TopUpAccount = ({
                               handleComplete,
                               handleClose,
                               selectedEmployees
                             }: AssignToPlanProps) => {
  const { accounts, plans, employerModules } = useData();
  const { data: features } = useFeatures();
  const [selectedAccount, setSelectedAccount] = useState<EmployeeAccount | null>(null);
  const [amount, setAmount] = useState<string>("");
  const [newBalance, setNewBalance] = useState<number | null>(null);
  const [error, setError] = useState<string>("");


  if(selectedEmployees.length !== 1){ handleClose()}

  var shouldShowLoadFee = features?.find((f) => f.name === "f_auto_load_fees" && f.isEnabled)

  const selectedEmployee = selectedEmployees[0] ?? {}
  const selectedPlanDetails = plans?.data?.items.find((x) => x.id === selectedAccount?.planId);
  const selectedPlanModule = employerModules?.data?.find((x) => x.employerModuleType === selectedPlanDetails?.employerModuleOptions.employerModuleType);
  const loadFeePercent = 
    selectedAccount?.employerModuleOptions.loadFeePercent ??
    selectedPlanDetails?.employerModuleOptions.loadFeePercent ?? 
    selectedPlanModule?.employerModuleConfig.loadFeePercent ?? 
    0;
                        
  // Filter accounts for the selected employee
  const employeeAccounts = accounts.data?.items.filter(
    (account) => account.employeeId === selectedEmployee.id
  ) ?? [];


  const topUpAccount = useTopUpAccount({
    onSuccess: () => {
      handleComplete();
    },
    onError: (error: Error) => {
      setError(error.message);
    }
  });

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (selectedAccount && amount) {
      const amountValue = Number(amount);
      if (amountValue <= 0) {
        setError("Please enter an amount greater than 0.");
        return;
      }
      topUpAccount.mutate({ selectedAccount, amount: amountValue });
    } else {
      setError("Please select an account and enter an amount.");
    }
  }

  useEffect(() => {
    if(!selectedAccount || !selectedAccount.availableBalance.amount) 
      return setNewBalance(null);
    
    if(amount) {
      setNewBalance(selectedAccount.availableBalance.amount + Number(amount))
    } else {
      setNewBalance(selectedAccount.availableBalance.amount);
    }
  }, [amount, selectedAccount])

  return (
    <>
      <NavBackButton onBackPress={handleClose} />
      <Box component={"form"} onSubmit={handleSubmit}>
        <DialogTitle>
          One-time top-up for {selectedEmployee.firstName} {selectedEmployee.lastName}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {error && (
              <Grid item xs={12}>
                <Typography color="error">{error}</Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography my={1}>Account</Typography>
              {employeeAccounts.length === 0 ? (
                <Typography color="error">
                  This employee does not have any associated accounts.
                </Typography>
              ) : (
                <Autocomplete
                  size="small"
                  options={employeeAccounts.sort((a, b) => a.employerModuleOptions.employerModuleType.localeCompare(b.employerModuleOptions.employerModuleType))}
                  getOptionLabel={(option) =>
                    `${option.name} - Balance: ${option.availableBalance.amount}`
                  }
                  value={selectedAccount}
                  onChange={(_, newValue) => setSelectedAccount(newValue)}
                  groupBy={(option) => toTitleCase(option.employerModuleOptions.employerModuleType)}
                  filterSelectedOptions
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select an account" />
                  )}
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          paddingLeft: 1,
                          paddingTop: 1,
                          fontSize: 14
                        }}
                      >
                        {params.group}
                      </Typography>
                      <ul style={{ paddingLeft: 20, margin: 0 }}>
                        {params.children}
                      </ul>
                    </li>
                  )}      
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography my={1}>Amount</Typography>
              <TextField
                fullWidth
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  if (error && Number(e.target.value) > 0) {
                    setError("");
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography color="gray">
                This amount will be added to the employee's account. It will expire or roll over when the account renews.
              </Typography>
            </Grid>

            {selectedAccount && <>
              <Grid item xs={12}>
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Typography color="gray">
                    Current Balance: 
                  </Typography>
                  <Typography>
                    {formatCurrency(selectedAccount?.availableBalance.amount)}
                  </Typography>
                </Stack>
                <Divider sx={{my: 2}} variant="fullWidth" flexItem/>
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Typography color="gray">
                    New Balance:
                  </Typography>
                  <Typography>
                    {formatCurrency(newBalance ?? selectedAccount?.availableBalance.amount)}
                  </Typography>
                </Stack>
                <Divider sx={{my: 2}} variant="fullWidth" flexItem/>
                { shouldShowLoadFee && 
                  <Stack direction={"row"} justifyContent={"space-between"}>
                    <Typography color="gray">
                      Load Fee ({(loadFeePercent * 100).toFixed(2)}%)
                    </Typography>
                    <LoadFeeTooltip />
                    <Typography>
                      ${Number(amount) * loadFeePercent}
                    </Typography>
                  </Stack>
                }
              </Grid>
            </>}
            
          </Grid>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            loading={topUpAccount.isLoading}
            disabled={!selectedAccount || !amount}
          >
            Top Up
          </LoadingButton>
        </DialogActions>
      </Box>
    </>
  );
};
